/*
 * Alaska Cold "Ask a question" engine (Step L, Phase 1). Loaded with defer before ac-estimator.js,
 * which draws the card and calls these functions.
 *
 * Rule-based only. Answers come from the knowledge index (/pages/knowledge, built from the site's own
 * pages, FAQs, settings and guides). A question the rules cannot match is handed to the team: nothing is
 * invented, no price is guessed and no place is claimed that the site does not name. docs/ask.md explains
 * the rules; tests/ac-ask.test.js checks them.
 */
(function (root) {
  'use strict';

  const MARKER = /\[CONFIRM WITH CLIENT[^\]]*\]/gi;

  /* ---------------- Text clean-up ---------------- */

  const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', rsquo: "'", lsquo: "'", ndash: '-', hellip: '...' };

  function decodeOnce(text) {
    return text
      .replace(/&#(\d+);/g, (match, code) => String.fromCharCode(Number(code)))
      .replace(/&#x([0-9a-f]+);/gi, (match, code) => String.fromCharCode(parseInt(code, 16)))
      .replace(/&([a-z]+);/gi, (match, name) => (ENTITIES[name.toLowerCase()] !== undefined ? ENTITIES[name.toLowerCase()] : match));
  }

  /* Text that was escaped twice (an answer saved with "&#39;" in it arrives as "&amp;#39;") needs a second pass. */
  function decodeEntities(text) {
    let value = String(text || '');
    for (let pass = 0; pass < 3; pass += 1) {
      const next = decodeOnce(value);
      if (next === value) break;
      value = next;
    }
    return value;
  }

  function wordCount(text) {
    return String(text || '').split(/\s+/).filter(Boolean).length;
  }

  /*
   * Removes [CONFIRM WITH CLIENT] markers. A clause that is only a label once the marker is gone
   * ("Parts and labour warranty period", "target response time") is dropped rather than shown as if it
   * were a fact. Returns the text and whether anything in it still needs the client's confirmation.
   */
  function cleanText(text) {
    const source = decodeEntities(text).replace(/\s+/g, ' ').trim();
    if (!source) return { text: '', unconfirmed: false };
    MARKER.lastIndex = 0;
    const unconfirmed = MARKER.test(source);
    MARKER.lastIndex = 0;
    if (!unconfirmed) return { text: source, unconfirmed: false };

    const sentences = source.split(/(?<=[.!?])\s+/);
    const kept = [];
    sentences.forEach((sentence) => {
      if (!sentence.match(MARKER)) {
        kept.push(sentence);
        return;
      }
      const clauses = sentence.split(/;\s*/).map((clause) => {
        if (!clause.match(MARKER)) return clause;
        const rest = clause.replace(MARKER, '').replace(/\s+([.,:;!?])/g, '$1').replace(/[\s:,]+([.!?]?)$/, '$1').trim();
        const bare = rest.replace(/[.!?]$/, '');
        if (!bare || (!/\d/.test(bare) && wordCount(bare) <= 6) || /:$/.test(bare)) return '';
        return rest;
      });
      let joined = clauses.filter(Boolean).join('; ').trim();
      if (!joined) return;
      if (!/[.!?]$/.test(joined) && /[.!?]$/.test(sentence)) joined += '.';
      if (wordCount(joined) >= 3) kept.push(joined);
    });
    return { text: kept.join(' ').replace(/\s+([.,;:!?])/g, '$1').trim(), unconfirmed: true };
  }

  /* ---------------- Normalising questions and documents ---------------- */

  /* Phrases first, so a multi-word name becomes one token: sector terms, common typos, then spelling. */
  const PHRASES = [
    [/\bwalk[\s-]*in[\s-]*(fridges?|chillers?|coolers?|cold rooms?|coldrooms?)([\s-]*rooms?)?\b/g, ' coldroom '],
    [/\bwalk[\s-]*in[\s-]*freezers?\b/g, ' freezerroom '],
    [/\b(cold[\s-]*rooms?|coldrooms?|cool[\s-]*rooms?|chill(er)?[\s-]*rooms?)\b/g, ' coldroom '],
    [/\bfreezer[\s-]*rooms?\b/g, ' freezerroom '],
    [/\bblast[\s-]*(freezers?|chillers?)\b/g, ' blastfreezer '],
    [/\bcold[\s-]*stor(age|es?)\b/g, ' coldstorage '],
    [/\bmulti[\s-]*decks?\b/g, ' multideck '],
    [/\bserve[\s-]*overs?\b/g, ' serveover '],
    [/\bdisplay[\s-]*(fridges?|cabinets?|chillers?|freezers?)\b/g, ' displayfridge fridge '],
    [/\bcondensing[\s-]*units?\b/g, ' condensingunit '],
    [/\bfit(ting)?[\s-]*outs?\b/g, ' shopfitting '],
    [/\bcall[\s-]*outs?\b/g, ' callout '],
    [/\bshop[\s-]*fit(ting|ters?|s)?\b/g, ' shopfitting '],
    [/\bnot[\s-]*(cooling|cold|getting cold|chilling|freezing)\b/g, ' notcooling '],
    [/\b(stopped|stops) (working|cooling)\b/g, ' notcooling '],
    [/\b(broken|broke) down\b|\bbreakdown\b/g, ' broken '],
    [/\bplanning permission\b/g, ' planningpermission '],
    [/\b(lead[\s-]*times?|how long|how soon|how quickly|how fast)\b/g, ' leadtime '],
    [/\b(pay monthly|monthly payments?|per month|a month|hire purchase|spread the cost)\b/g, ' finance '],
    [/\b(site visit|site survey)\b/g, ' survey '],
    [/\b(annual|regular|routine) service\b|\bservice contracts?\b|\bserviced\b/g, ' maintenance '],
    [/\b24\s*\/\s*7\b|\b24 hours?\b|\bout of hours\b/g, ' allhours '],
    [/\bf[\s-]*gas\b/g, ' fgas '],
    [/\bsecond[\s-]*hand\b|\bpre[\s-]*owned\b|\bex[\s-]*display\b/g, ' secondhand '],
  ];

  const TYPOS = {
    refridgeration: 'refrigeration', refrigiration: 'refrigeration', refrigaration: 'refrigeration', refrigeraton: 'refrigeration',
    refridgerator: 'fridge', refrigerator: 'fridge', fridgerator: 'fridge', freezor: 'freezer', freazer: 'freezer', freeser: 'freezer',
    maintainance: 'maintenance', maintanance: 'maintenance', maintenence: 'maintenance', maintainence: 'maintenance',
    warrenty: 'warranty', warranti: 'warranty', waranty: 'warranty', guarentee: 'guarantee', gaurantee: 'guarantee', garantee: 'guarantee',
    instalation: 'installation', installtion: 'installation', instillation: 'installation', survay: 'survey', servey: 'survey',
    tempreture: 'temperature', temprature: 'temperature', temperture: 'temperature', compresser: 'compressor', evaporater: 'evaporator',
    quotation: 'quote', quotations: 'quote', quoted: 'quote', costs: 'cost', pricing: 'price', prices: 'price',
  };

  /* American spellings to British, both ways round the site uses British only. */
  const SPELLING = {
    center: 'centre', centers: 'centres', meter: 'metre', meters: 'metres', liter: 'litre', liters: 'litres', color: 'colour',
    organization: 'organisation', customize: 'customise', aluminum: 'aluminium', gray: 'grey', labor: 'labour', neighbor: 'neighbour',
    catalog: 'catalogue', license: 'licence', analyze: 'analyse', favor: 'favour', defense: 'defence', authorized: 'authorised',
    insulated: 'insulated', optimize: 'optimise', program: 'programme', mold: 'mould', moldy: 'mouldy', odor: 'odour', fiber: 'fibre',
  };

  /* Concepts: words that mean the same thing for matching purposes. */
  const CONCEPTS = {
    coldroom: ['coldroom', 'chiller', 'chillroom', 'cooler'],
    freezerroom: ['freezerroom'],
    freezer: ['freezer', 'frozen', 'freeze', 'freezing'],
    display: ['displayfridge', 'display', 'cabinet'],
    repair: ['repair', 'broken', 'fix', 'fault', 'faulty', 'notcooling', 'engineer', 'callout'],
    maintenance: ['maintenance', 'maintain', 'servicing', 'maintained', 'service'],
    price: ['price', 'cost', 'expensive', 'cheap', 'budget', 'afford', 'charge', 'fee'],
    rent: ['hire', 'rent', 'rental', 'renting', 'hired'],
    size: ['size', 'fit', 'dimension', 'big', 'space', 'sqm', 'm2', 'large', 'small'],
    warranty: ['warranty', 'guarantee'],
    survey: ['survey', 'surveyor'],
    leadtime: ['leadtime', 'quick', 'quickly', 'soon', 'week', 'take', 'time'],
    install: ['install', 'installer', 'fitted', 'build', 'built'],
    planning: ['planningpermission', 'regulation', 'regulations', 'legal', 'law'],
    finance: ['finance', 'leasing', 'lease', 'credit', 'loan', 'monthly'],
    temperature: ['temperature', 'degree', 'degrees', 'celsius'],
    emergency: ['emergency', 'urgent', 'allhours', 'weekend', 'night'],
    fgas: ['fgas', 'refrigerant', 'refrigerants'],
    certified: ['certified', 'registered', 'register', 'accredited', 'accreditation', 'qualified', 'qualification', 'certificate'],
  };

  /* Product words that keep their own meaning (a question about multidecks is not about every display). */
  const PRODUCT_TOKENS = new Set(['multideck', 'serveover', 'blastfreezer', 'freezerroom', 'coldstorage', 'condensingunit', 'shopfitting', 'fridge', 'evaporator', 'compressor', 'panel', 'door', 'floor', 'refriger', 'quote', 'coldroom', 'display']);

  /* Products a question can be about. An answer about a different product scores lower. */
  const PRODUCT_FAMILY = {
    coldroom: 'rooms', freezerroom: 'rooms', blastfreezer: 'rooms', coldstorage: 'rooms',
    display: 'display', multideck: 'display', serveover: 'display', shopfitting: 'display',
  };

  const CONCEPT_OF = {};
  Object.keys(CONCEPTS).forEach((concept) => CONCEPTS[concept].forEach((word) => (CONCEPT_OF[word] = concept)));

  const STOPWORDS = new Set(
    (
      'a an the and or but if of to in on at by for with from into about as is are was were be been being am do does did done doing ' +
      'i me my we our us you your it its this that these those there here what which who whom whose when where why how can could ' +
      'will would shall should may might must have has had having get got any some all much many more most very really just also ' +
      'please hi hello thanks thank tell know want like need looking look help happen happens anything something someone ' +
      'one ones not no yes ok okay so than then too up out off over again further once same other own such only s t ' +
      'mean meaning means thing things stuff way ways use using used offer offers provide provides give gives'
    ).split(' ')
  );

  /* Words made from phrases keep their exact form. */
  const NO_STEM = new Set(['coldroom', 'freezerroom', 'blastfreezer', 'coldstorage', 'multideck', 'serveover', 'displayfridge', 'condensingunit', 'shopfitting', 'notcooling', 'planningpermission', 'leadtime', 'allhours', 'fgas', 'secondhand', 'callout', 'always']);

  function stem(word) {
    if (word.length <= 3 || CONCEPT_OF[word] || NO_STEM.has(word)) return word;
    if (word.endsWith('ies') && word.length > 4) return `${word.slice(0, -3)}y`;
    if (word.endsWith('ation') && word.length > 7) return word.slice(0, -5);
    if (word.endsWith('ing') && word.length > 5) return word.slice(0, -3);
    if (word.endsWith('ed') && word.length > 4) return word.slice(0, -2);
    if (/(ss|us|is)$/.test(word)) return word;
    if (word.endsWith('es') && /(sh|ch|x|s)es$/.test(word)) return word.slice(0, -2);
    if (word.endsWith('s')) return word.slice(0, -1);
    return word;
  }

  function normalise(text) {
    let value = ` ${decodeEntities(text).toLowerCase().replace(/[’‘`]/g, "'")} `;
    value = value.replace(/[^a-z0-9£'/\s-]/g, ' ').replace(/'s\b/g, '').replace(/'/g, '');
    value = value
      .split(/\s+/)
      .map((word) => TYPOS[word] || SPELLING[word] || word)
      .join(' ');
    PHRASES.forEach(([pattern, replacement]) => {
      value = value.replace(pattern, replacement);
    });
    return value.replace(/[/-]/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function tokens(text) {
    return normalise(text)
      .split(' ')
      .filter((word) => word && !STOPWORDS.has(word))
      .map((word) => {
        const stemmed = stem(word);
        return CONCEPT_OF[word] || CONCEPT_OF[stemmed] || stemmed;
      });
  }

  /* Safe for analytics: no email address, phone number or full postcode, short. */
  function analyticsText(question) {
    return normalise(
      String(question || '')
        .replace(/\S+@\S+/g, ' ')
        .replace(/\+?\d[\d\s]{6,}\d/g, ' ')
        .replace(/\b([A-Z]{1,2}\d[A-Z\d]?)\s*\d[A-Z]{2}\b/gi, '$1')
    ).slice(0, 100);
  }

  /* ---------------- The knowledge index, prepared for matching ---------------- */

  const SERVICE_PAGE_FOR = {
    cold_room: 'cold-rooms',
    freezer_room: 'freezer-rooms',
    combined: 'cold-rooms',
    cold_storage_plant: 'commercial-cold-storage',
    display: 'supermarket-fittings',
  };

  function prepare(knowledge) {
    const faqs = [];
    const seen = new Set();
    (knowledge.faqs || []).forEach((faq) => {
      if (seen.has(faq.id)) return;
      seen.add(faq.id);
      const answer = cleanText(faq.a);
      if (!answer.text) return;
      faqs.push({
        kind: 'faq',
        id: faq.id,
        question: decodeEntities(faq.q),
        text: answer.text,
        unconfirmed: answer.unconfirmed,
        url: faq.url,
        page: faq.page,
        pageTitle: decodeEntities(faq.page_title),
      });
    });

    const docs = faqs.slice();
    const pages = knowledge.pages || [];
    pages.forEach((page) => {
      /* Only FAQ answers are shown without a [CONFIRM WITH CLIENT] marker. A page paragraph, specification
         row or statement that still carries one is left out until the client confirms it. */
      const intro = cleanText(page.intro);
      if (intro.text && !intro.unconfirmed) {
        docs.push({ kind: 'page', id: `page:${page.handle}`, question: decodeEntities(page.title), text: intro.text, unconfirmed: intro.unconfirmed, url: page.url, page: page.handle, pageTitle: decodeEntities(page.title) });
      }
      const rows = page.specs && Array.isArray(page.specs.rows) ? page.specs.rows : [];
      rows.forEach((row, index) => {
        const value = cleanText(row.value);
        if (!value.text || value.unconfirmed) return;
        docs.push({
          kind: 'spec',
          id: `spec:${page.handle}:${index}`,
          question: `${decodeEntities(page.title)}: ${decodeEntities(row.label)}`,
          label: decodeEntities(row.label),
          text: `${decodeEntities(row.label)}: ${value.text}`,
          unconfirmed: value.unconfirmed,
          url: page.url,
          page: page.handle,
          pageTitle: decodeEntities(page.title),
        });
      });
    });

    (knowledge.statements || []).forEach((statement, index) => {
      const value = cleanText(statement.text);
      if (!value.text || value.unconfirmed || wordCount(value.text) < 6) return;
      docs.push({ kind: 'statement', id: `statement:${index}`, question: '', text: value.text.replace(/([^.!?])$/, '$1.'), unconfirmed: value.unconfirmed, url: statement.url, page: statement.page, pageTitle: decodeEntities(statement.page_title) });
    });

    (knowledge.guides || []).forEach((guide) => {
      const facts = (guide.facts || []).map((fact) => cleanText(fact).text).filter(Boolean);
      if (!facts.length) return;
      docs.push({ kind: 'guide', id: `guide:${guide.handle}`, question: decodeEntities(guide.title), text: facts.join(' '), facts, unconfirmed: false, url: guide.url, page: guide.handle, pageTitle: decodeEntities(guide.title) });
    });

    /* A statement is its own headline, so its words count as fully as a FAQ question's. */
    docs.forEach((doc) => {
      doc.titleList = tokens(doc.kind === 'statement' ? doc.text : doc.question);
      doc.titleTokens = new Set(doc.titleList);
      doc.bodyTokens = new Set(tokens(doc.text));
    });

    const df = {};
    docs.forEach((doc) => {
      new Set([...doc.titleTokens, ...doc.bodyTokens]).forEach((token) => {
        df[token] = (df[token] || 0) + 1;
      });
    });

    return { knowledge, docs, faqs, pages, df, total: docs.length, business: knowledge.business || {} };
  }

  function idf(index, token) {
    return Math.log(1 + index.total / (1 + (index.df[token] || 0)));
  }

  /* ---------------- Scoring ---------------- */

  const KIND_BONUS = { faq: 0.06, spec: 0.03, guide: 0.02, page: 0, statement: 0 };
  const THRESHOLD = 0.5;
  const RELATED_THRESHOLD = 0.34;

  function scoreDocs(index, question, filter) {
    const queryTokens = Array.from(new Set(tokens(question)));
    if (!queryTokens.length) return { queryTokens, ranked: [] };
    const weights = queryTokens.map((token) => idf(index, token));
    const possible = weights.reduce((sum, weight) => sum + weight * 3, 0);

    const ranked = index.docs
      .filter((doc) => !filter || filter(doc))
      .map((doc) => {
        let got = 0;
        let matched = 0;
        queryTokens.forEach((token, position) => {
          const family = PRODUCT_FAMILY[token];
          if (doc.titleTokens.has(token)) {
            got += weights[position] * 3;
            matched += 1;
          } else if (family && Array.from(doc.titleTokens).some((other) => PRODUCT_FAMILY[other] === family)) {
            got += weights[position] * 2;
            matched += 1;
          } else if (doc.bodyTokens.has(token)) {
            got += weights[position];
            matched += 1;
          }
        });
        let score = possible ? got / possible + (matched ? KIND_BONUS[doc.kind] || 0 : 0) : 0;
        const askedFamilies = new Set(queryTokens.map((token) => PRODUCT_FAMILY[token]).filter(Boolean));
        const titledFamilies = new Set(Array.from(doc.titleTokens).map((token) => PRODUCT_FAMILY[token]).filter(Boolean));
        if (askedFamilies.size && titledFamilies.size && ![...askedFamilies].some((family) => titledFamilies.has(family))) score *= 0.45;
        const first = doc.titleList.findIndex((token) => queryTokens.includes(token));
        return { doc, score, matched, first: first === -1 ? 99 : first };
      })
      .filter((entry) => entry.matched > 0)
      /* Equal scores: the text that names the subject earliest, then the shorter one. */
      .sort((a, b) => b.score - a.score || a.first - b.first || a.doc.titleList.length - b.doc.titleList.length);
    return { queryTokens, ranked };
  }

  function bucket(score) {
    if (score >= 0.7) return 'high';
    if (score >= THRESHOLD) return 'mid';
    if (score >= 0.2) return 'low';
    return 'none';
  }

  function answerFromDoc(doc) {
    return {
      text: doc.text,
      facts: doc.facts || null,
      url: doc.url,
      sourceTitle: doc.pageTitle,
      question: doc.question,
      id: doc.id,
      kind: doc.kind,
      unconfirmed: doc.unconfirmed,
    };
  }

  function relatedFor(ranked, used) {
    const related = [];
    ranked.forEach((entry) => {
      if (related.length >= 2 || entry.doc.kind !== 'faq' || entry.score < RELATED_THRESHOLD || used.has(entry.doc.id)) return;
      used.add(entry.doc.id);
      related.push({ id: entry.doc.id, question: entry.doc.question });
    });
    return related;
  }

  /* ---------------- Places ---------------- */

  /* London boroughs: the London page says "We cover all 32 London boroughs and the City of London". */
  const LONDON_BOROUGHS = [
    'barking and dagenham', 'barking', 'dagenham', 'barnet', 'bexley', 'brent', 'bromley', 'camden', 'croydon', 'ealing', 'enfield', 'greenwich',
    'hackney', 'hammersmith and fulham', 'hammersmith', 'fulham', 'haringey', 'harrow', 'havering', 'hillingdon', 'hounslow', 'islington',
    'kensington and chelsea', 'kensington', 'chelsea', 'kingston upon thames', 'kingston', 'lambeth', 'lewisham', 'merton', 'newham', 'redbridge',
    'richmond upon thames', 'richmond', 'southwark', 'sutton', 'tower hamlets', 'waltham forest', 'wandsworth', 'westminster', 'city of london',
  ];

  /* Reference list of UK places and counties (not claims): used only to tell a real UK place from an unknown word. */
  const UK_PLACES = {
    england: (
      'manchester,birmingham,liverpool,leeds,sheffield,bristol,newcastle,nottingham,leicester,coventry,bradford,hull,stoke,wolverhampton,derby,' +
      'southampton,portsmouth,plymouth,exeter,norwich,ipswich,cambridge,oxford,reading,slough,milton keynes,luton,bedford,northampton,peterborough,' +
      'brighton,hove,worthing,crawley,eastbourne,hastings,chichester,bournemouth,poole,swindon,bath,gloucester,cheltenham,worcester,hereford,' +
      'shrewsbury,telford,stafford,lincoln,york,harrogate,scarborough,middlesbrough,sunderland,durham,carlisle,lancaster,preston,blackpool,bolton,' +
      'wigan,warrington,chester,stockport,oldham,rochdale,huddersfield,wakefield,doncaster,rotherham,barnsley,grimsby,scunthorpe,mansfield,' +
      'chesterfield,high wycombe,aylesbury,basingstoke,winchester,salisbury,newbury,bracknell,maidenhead,windsor,guildford,woking,watford,' +
      'st albans,stevenage,hemel hempstead,hatfield,hertford,welwyn garden city,harlow,chelmsford,colchester,southend,basildon,brentwood,' +
      'canterbury,maidstone,dover,folkestone,ashford,tunbridge wells,tonbridge,sevenoaks,dartford,gravesend,margate,ramsgate,medway,chatham,' +
      'rochester,gillingham,redhill,reigate,epsom,leatherhead,staines,heathrow,truro,cornwall,devon,somerset,dorset,wiltshire,hampshire,' +
      'berkshire,buckinghamshire,bedfordshire,hertfordshire,cambridgeshire,norfolk,suffolk,lincolnshire,nottinghamshire,derbyshire,' +
      'leicestershire,northamptonshire,warwickshire,staffordshire,shropshire,cheshire,lancashire,yorkshire,cumbria,northumberland,essex,kent,' +
      'surrey,sussex,east sussex,west sussex,oxfordshire,gloucestershire,worcestershire,herefordshire,middlesex,isle of wight,midlands,' +
      'west midlands,east midlands,the north,north east,north west,south west,east anglia,home counties,england'
    ).split(','),
    scotland: 'glasgow,edinburgh,aberdeen,dundee,inverness,stirling,perth,paisley,fife,ayr,scotland'.split(','),
    wales: 'cardiff,swansea,newport,wrexham,bangor,aberystwyth,llandudno,wales'.split(','),
    northern_ireland: 'belfast,derry,londonderry,lisburn,newry,armagh,omagh,northern ireland'.split(','),
  };

  const LONDON_POSTCODE_AREAS = ['E', 'EC', 'N', 'NW', 'SE', 'SW', 'W', 'WC'];

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* One to three word runs from the question, longest first, skipping runs that start or end with a common word. */
  function candidatePhrases(question) {
    const words = decodeEntities(question)
      .replace(/[^A-Za-z0-9'\s-]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);
    const phrases = [];
    for (let size = 3; size >= 1; size -= 1) {
      for (let start = 0; start + size <= words.length; start += 1) {
        const slice = words.slice(start, start + size);
        if (STOPWORDS.has(slice[0].toLowerCase()) && slice[0].toLowerCase() !== 'st') continue;
        if (STOPWORDS.has(slice[slice.length - 1].toLowerCase())) continue;
        phrases.push(slice.join(' '));
      }
    }
    return phrases;
  }

  const COVERAGE_WORDS = /\b(cover|covers|covered|covering|work|works|working|install|installs|come|comes|travel|near|nearby|area|areas|serve|serves|serving|based|go|operate|local|visit|reach|postcode)\b/i;

  /*
   * Place names in the question. A run counts as a place when the site's area pages always write it as a
   * proper name (capital letter, never in lower case, so "cover" or "cold room" never count), or when it is
   * in the reference list of UK places. Either way the question has to be about where (a word such as
   * cover, work, install, near) or the visitor wrote the name with a capital letter.
   */
  function placesIn(index, question) {
    const corpus = areaCorpus(index);
    const asksWhere = COVERAGE_WORDS.test(question);
    const found = [];
    candidatePhrases(question).forEach((phrase) => {
      if (phrase.length < 3 || found.some((item) => item.phrase.toLowerCase().includes(phrase.toLowerCase()))) return;
      const capitalised = /^[A-Z]/.test(phrase);
      if (!asksWhere && !capitalised) return;
      const lower = phrase.toLowerCase();
      const proper = titleCase(lower);
      const named = new RegExp(`\\b${escapeRegExp(proper)}\\b`).test(corpus) && !new RegExp(`\\b${escapeRegExp(lower)}\\b`).test(corpus);
      const nation = Object.keys(UK_PLACES).find((key) => UK_PLACES[key].includes(lower));
      const borough = LONDON_BOROUGHS.includes(lower);
      if (named || nation || borough) found.push({ phrase: proper, lower, named, nation: nation || (borough ? 'england' : ''), borough });
    });
    return found;
  }

  /*
   * The text where the site lists the places it works: each area page's first paragraph and its "Where we
   * work" section, the areas page's county sections, and the area FAQ questions. Other area text (repairs,
   * shop fitting) is left out, so names like F-Gas or ULEZ are never mistaken for places.
   */
  function areaCorpus(index) {
    if (index.areaCorpus !== undefined) return index.areaCorpus;
    const parts = areaSources(index)
      .filter((source) => (source.page.hub ? !/not sure/i.test(source.heading) : source.heading === '' || /^where we work/i.test(source.heading)))
      .map((source) => source.text.replace(/\b(Alaska Cold|Great Portland Street|F-Gas|ULEZ|Congestion Charge)\b/g, ' '));
    index.faqs.filter((faq) => /area|london|essex|kent|surrey/i.test(faq.page)).forEach((faq) => parts.push(faq.question));
    index.areaCorpus = parts.join(' \n ');
    return index.areaCorpus;
  }

  function areaSources(index) {
    if (index.areaSources) return index.areaSources;
    const sources = [];
    index.pages
      .filter((page) => page.type === 'area')
      .forEach((page) => {
        const blocks = [{ heading: '', text: page.intro }].concat(page.sections || []);
        blocks.forEach((block) => {
          if (!block.text) return;
          sources.push({ page, heading: block.heading || '', text: decodeEntities(block.text) });
        });
      });
    index.areaSources = sources;
    return sources;
  }

  function sentenceWith(text, phrase) {
    const pattern = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, 'i');
    const sentence = text.split(/(?<=[.!?])\s+/).find((item) => pattern.test(item)) || '';
    return cleanText(sentence).text;
  }

  function areaPageFor(index, name) {
    const lower = name.toLowerCase();
    return index.pages.find((page) => page.type === 'area' && !page.hub && page.title.toLowerCase().includes(lower)) || null;
  }

  /* Where the site says it works for a place: FAQ questions, area pages and the areas hub, as written. */
  function coverageFor(index, question) {
    const raw = decodeEntities(question);
    const upper = raw.toUpperCase().replace(/[^A-Z0-9\s]/g, ' ');
    const postcodeMatch = /\b([A-Z]{1,2})(\d[A-Z\d]?)(?:\s*\d[A-Z]{2})?\b/.exec(upper);
    const postcode = postcodeMatch && COVERAGE_WORDS.test(raw) ? postcodeMatch : null;
    const places = placesIn(index, raw);
    const sources = areaSources(index);
    const hub = index.pages.find((page) => page.type === 'area' && page.hub) || null;
    const london = index.pages.find((page) => page.type === 'area' && !page.hub && /london/i.test(page.title));

    for (const place of places) {
      const pattern = new RegExp(`\\b${escapeRegExp(place.lower)}\\b`, 'i');
      const ownPage = index.pages.find((page) => page.type === 'area' && !page.hub && pattern.test(page.title));

      // 1. A FAQ that names the place in its question, preferring the place's own area page
      const faqs = index.faqs.filter((item) => pattern.test(item.question) && /(cover|work in|install|areas?|outside|near|reach)/i.test(item.question));
      faqs.sort((a, b) => Number(Boolean(ownPage && b.url === ownPage.url)) - Number(Boolean(ownPage && a.url === ownPage.url)));
      if (faqs.length) {
        return { status: 'named', place: place.phrase, answer: answerFromDoc(faqs[0]), links: linksFor(index, place.lower, sources, hub, ownPage ? ownPage.url : faqs[0].url) };
      }

      // 2. Area pages and the hub that name the place
      if (place.named) {
        const hits = sources.filter((source) => pattern.test(source.text));
        const first = ownPage ? { page: ownPage, text: ownPage.intro } : hits.find((source) => !source.page.hub) || hits[0];
        if (first) {
          const text = sentenceWith(first.text, place.lower) || cleanText(first.text).text;
          return {
            status: 'named',
            place: place.phrase,
            answer: { text: `Yes. ${text}`, url: first.page.url, sourceTitle: first.page.title, kind: 'coverage', unconfirmed: false },
            links: linksFor(index, place.lower, sources, hub, first.page.url),
          };
        }
      }

      // 3. A London borough the London page does not list by name: it covers all 32 boroughs
      if (place.borough && london) return londonAnswer(sources, london, place.phrase);
    }

    // 4. A London postcode, or a postcode area the areas page lists ("Postcode areas RM, IG, CM, SS and CO")
    if (postcode && london && LONDON_POSTCODE_AREAS.includes(postcode[1])) return londonAnswer(sources, london, `${postcode[1]}${postcode[2]}`);
    if (postcode && hub) {
      const hit = (hub.sections || []).find((section) => {
        const match = /Postcode areas ([A-Z ,and]+)/.exec(section.text || '');
        return match && match[1].split(/,|\band\b/).map((code) => code.trim()).includes(postcode[1]);
      });
      if (hit) {
        const page = areaPageFor(index, hit.heading);
        return {
          status: 'named',
          place: `${postcode[1]}${postcode[2]}`,
          answer: { text: `Yes. ${hit.heading}: ${sentenceWith(hit.text, 'Postcode areas')}`, url: page ? page.url : hub.url, sourceTitle: page ? page.title : hub.title, kind: 'coverage', unconfirmed: false },
          links: [page ? { label: page.title, url: page.url } : { label: hub.title, url: hub.url }],
        };
      }
    }

    // 5. A UK place the site does not name: the site's own words for the rest of the UK
    const ukPlace = places.find((place) => place.nation && place.nation !== 'northern_ireland');
    const ukPostcode = postcode && !['BT', 'JE', 'GY', 'IM'].includes(postcode[1]);
    if (ukPlace || ukPostcode) {
      const place = ukPlace ? ukPlace.phrase : `${postcode[1]}${postcode[2]}`;
      const rest = hub && (hub.sections || []).find((section) => /rest of the uk/i.test(section.heading));
      if (rest) {
        return {
          status: 'uk',
          place,
          answer: { text: `${cleanText(rest.text).text} Call us to confirm travel time to ${place}.`, url: hub.url, sourceTitle: hub.title, kind: 'coverage', unconfirmed: /CONFIRM/.test(rest.text) },
          links: [{ label: hub.title, url: hub.url }],
        };
      }
    }

    if (places.some((place) => place.nation === 'northern_ireland') || (postcode && postcode[1] === 'BT')) return { status: 'unknown', place: '' };
    return null;
  }

  function londonAnswer(sources, london, place) {
    const text = sources.map((source) => sentenceWith(source.text, 'boroughs')).find(Boolean) || cleanText(london.intro).text;
    return {
      status: 'named',
      place,
      answer: { text: `Yes. ${text}`, url: london.url, sourceTitle: london.title, kind: 'coverage', unconfirmed: false },
      links: [{ label: london.title, url: london.url }],
    };
  }

  function titleCase(text) {
    return String(text)
      .toLowerCase()
      .replace(/\b([a-z])/g, (letter) => letter.toUpperCase())
      .replace(/\b(And|Upon|Of|The)\b/g, (word) => word.toLowerCase());
  }

  function linksFor(index, phrase, sources, hub, firstUrl) {
    const pattern = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, 'i');
    const links = [];
    const add = (page) => {
      if (page && !links.some((link) => link.url === page.url)) links.push({ label: page.title, url: page.url });
    };
    const firstPage = index.pages.find((page) => page.url === firstUrl);
    if (firstPage && firstPage.type === 'area') add(firstPage);
    sources.forEach((source) => {
      if (!pattern.test(source.text)) return;
      if (!source.page.hub) add(source.page);
      else add(areaPageFor(index, source.heading) || (/london/i.test(source.heading) ? areaPageFor(index, 'London') : hub));
    });
    const lower = ` ${phrase.toLowerCase()} `;
    if (LONDON_BOROUGHS.some((name) => lower.includes(` ${name} `))) add(areaPageFor(index, 'London'));
    index.pages.filter((page) => page.type === 'area' && !page.hub && pattern.test(page.title)).forEach(add);
    return links.slice(0, 3);
  }

  /* ---------------- Intents ---------------- */

  const PATTERNS = {
    emergency: /\b(broken|notcooling|not working|alarm|alarms|beeping|warming up|too warm|getting warm|temperature (is )?(rising|going up|high)|leaking|leak|water on the floor|iced up|icing up|ice build|frozen up|no power|tripp(ed|ing)|won't (start|turn on|close)|stock (is |at )?(risk|warm|spoil|going off|thaw|melt))/,
    stockRisk: /\bstock\b.*\b(risk|warm|spoil|going off|thaw|melt|losing|lose)\b/,
    price: /\b(price|cost|how much|budget|expensive|cheap|cheaper|afford|ballpark)\b|£/,
    priceExclude: /\b(repair|fix|broken|maintenance|servicing|callout|call out|running|electricity|energy|run|finance)\b/,
    size: /\b(what size|which size|how big|sizes?|sizing|dimensions?|how much space|will it fit|fit in|fit into|square met(re|er)s?|square feet|sq ?ft|m2)\b/,
    finance: /\b(finance|financing|leasing|lease|credit|loan|instal?ments?)\b/,
    hours: /\b(opening hours|office hours|opening times|what time|open on|are you open|when are you open|when do you (open|close)|closing time|open today|open tomorrow|weekends?|saturdays?|sundays?|bank holidays?)\b/,
    phone: /\b(phone|telephone|contact number|your number|phone number|ring you|call you on|number to call|mobile number)\b/,
    email: /\b(email|e mail|mail address)\b/,
    address: /\b(address|where are you|where is your office|where are you based|based|located|location|your office|head office|visit you)\b/,
    whatsapp: /\b(whatsapp|whats app)\b/,
    coverage: /\b(cover|covers|covering|work in|working in|install in|come to|travel to|operate in|serve|serving|near|nearby|local to|areas?|go to|out to|in my area)\b/,
    greeting: /^(hi|hello|hey|good (morning|afternoon|evening)|hiya|thanks|thank you)\b/,
  };

  const FAULT_FROM = [
    [/\b(alarm|beeping|error|code)\b/, 'alarm'],
    [/\b(water|leak|leaking|puddle)\b/, 'water'],
    [/\b(ice|icing|iced|frost)\b/, 'ice'],
    [/\b(noise|noisy|loud|rattl|running (all the time|constantly))\b/, 'noise'],
    [/\b(door|seal|gasket)\b/, 'door'],
    [/\b(dead|no power|tripp|won't (start|turn on))\b/, 'dead'],
    [/\b(notcooling|warm|temperature|broken|not working)\b/, 'not_cooling'],
  ];

  function faultFor(normalised) {
    const found = FAULT_FROM.find(([pattern]) => pattern.test(normalised));
    return found ? found[1] : 'not_cooling';
  }

  function result(fields) {
    return Object.assign(
      { intent: 'content', matched: false, score: 0, bucket: 'none', answer: null, module: null, moduleOptions: null, related: [], handoff: false, facts: null, links: [] },
      fields
    );
  }

  function factAnswer(index, kind) {
    const business = index.business;
    const contactUrl = business.contact_url || '/pages/contact';
    const allHours = index.docs.find((doc) => doc.kind === 'statement' && /24 hours a day/i.test(doc.text) && doc.page === 'contact') ||
      index.docs.find((doc) => doc.kind === 'statement' && /24 hours a day/i.test(doc.text));
    const emergency = allHours ? allHours.text : '';
    const address = [business.street, [business.locality, business.postcode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const texts = {
      hours: `Office hours: ${(business.opening_hours || []).join('. ')}.${emergency ? ` ${emergency}` : ''}`,
      phone: `Call us on ${business.phone}.${emergency ? ` ${emergency}` : ''}`,
      email: `Email us at ${business.email}.`,
      address: `${business.name}, ${address}.`,
      whatsapp: `Send us a message or photos on WhatsApp: ${business.phone}.`,
    };
    const links = {
      phone: [{ label: `Call ${business.phone}`, url: business.phone_href }],
      email: [{ label: business.email, url: `mailto:${business.email}` }],
      whatsapp: [{ label: 'Open WhatsApp', url: business.whatsapp_url }],
      hours: [],
      address: [],
    };
    return result({
      intent: kind,
      matched: true,
      score: 1,
      bucket: 'high',
      answer: { text: texts[kind].replace(/\.\./g, '.'), url: contactUrl, sourceTitle: 'Contact Alaska Cold', kind: 'fact', unconfirmed: false },
      links: links[kind],
    });
  }

  /*
   * Answers one question. context: { financeOn, estimate: { need, area } | null, pageType, handle }.
   * Returns { intent, matched, score, bucket, answer, module, moduleOptions, related, handoff, links }.
   */
  function answer(index, question, context) {
    const ctx = context || {};
    const normalised = normalise(question);
    const raw = String(question || '').trim();
    if (!normalised) return result({ intent: 'empty', handoff: false });

    if (PATTERNS.greeting.test(normalised) && tokens(question).length <= 1) {
      return result({ intent: 'greeting', matched: true, score: 1, bucket: 'high', answer: { text: 'Ask about our services, the areas we cover, prices, sizes or opening hours, or pick a question below.', url: '', sourceTitle: '', kind: 'help' } });
    }

    const scored = scoreDocs(index, question);
    const best = scored.ranked[0];
    const used = new Set();

    /* 0. The exact question of a FAQ (a suggested question, or typed word for word): that FAQ's own answer,
          with the matching tool offered when the question is about a price, a size or a fault. */
    const exact = index.faqs.find((faq) => normalise(faq.question) === normalised);
    if (exact) {
      used.add(exact.id);
      let module = null;
      let moduleOptions = null;
      if (PATTERNS.emergency.test(normalised)) {
        module = 'repair';
        moduleOptions = { fault: faultFor(normalised) };
      } else if (PATTERNS.price.test(normalised) && !PATTERNS.priceExclude.test(normalised)) module = 'estimate';
      else if (PATTERNS.size.test(normalised)) module = 'sizing';
      else if (PATTERNS.finance.test(normalised) && ctx.financeOn) module = 'finance';
      return result({ intent: 'faq', matched: true, score: 1, bucket: 'high', answer: answerFromDoc(exact), module, moduleOptions, related: relatedFor(scored.ranked, used) });
    }

    // 1. Emergencies: the fault checker
    if (PATTERNS.emergency.test(normalised) || PATTERNS.stockRisk.test(normalised)) {
      const fault = faultFor(normalised);
      return result({
        intent: 'emergency',
        matched: true,
        score: best ? best.score : 0,
        bucket: 'high',
        module: 'repair',
        moduleOptions: { fault },
        answer: {
          text: 'That sounds urgent. The fault checker shows what to do right now to protect your stock, and the fastest way to reach an engineer.',
          url: '/pages/refrigeration-repair',
          sourceTitle: 'Commercial fridge and cold room repair',
          kind: 'module',
        },
      });
    }

    // 2. A follow-up about lead time for the size just estimated
    if (ctx.estimate && /\bleadtime\b/.test(normalised)) {
      const handle = SERVICE_PAGE_FOR[ctx.estimate.need] || 'cold-rooms';
      const rows = index.docs.filter((doc) => doc.kind === 'spec' && doc.page === handle && /installation time|lead time|programme/i.test(doc.label));
      if (rows.length) {
        return result({
          intent: 'leadtime',
          matched: true,
          score: 1,
          bucket: 'high',
          answer: { text: rows.map((row) => row.text).join(' '), url: rows[0].url, sourceTitle: rows[0].pageTitle, kind: 'spec', unconfirmed: rows.some((row) => row.unconfirmed) },
        });
      }
    }

    // 3. Prices for a new installation: the budget estimate
    if (PATTERNS.price.test(normalised) && !PATTERNS.priceExclude.test(normalised) && !PATTERNS.finance.test(normalised) && !/finance/.test(normalised)) {
      const sizeAsked = /how much space|how much room/.test(normalised);
      if (!sizeAsked) {
        return result({
          intent: 'price',
          matched: true,
          score: 1,
          bucket: 'high',
          module: 'estimate',
          answer: { text: 'We give budget ranges, subject to survey. Use the estimate tool:', url: '', sourceTitle: '', kind: 'module' },
          related: relatedFor(scoreDocs(index, `${question} cost`).ranked, used),
        });
      }
    }

    // 4. Sizes: what size do I need
    if (PATTERNS.size.test(normalised)) {
      return result({
        intent: 'size',
        matched: true,
        score: 1,
        bucket: 'high',
        module: 'sizing',
        answer: { text: 'Work out the size from what you store, and check it fits your space:', url: '', sourceTitle: '', kind: 'module' },
        related: relatedFor(scoreDocs(index, `${question} size`).ranked, used),
      });
    }

    // 5. Finance: the module when it is switched on, otherwise the finance guide's own answers
    if (PATTERNS.finance.test(normalised) || /\bfinance\b/.test(normalised)) {
      if (ctx.financeOn) {
        return result({
          intent: 'finance',
          matched: true,
          score: 1,
          bucket: 'high',
          module: 'finance',
          answer: { text: 'See an illustrative monthly figure, subject to status and survey:', url: '', sourceTitle: '', kind: 'module' },
        });
      }
      const financeDocs = scoreDocs(index, `${question} finance`, (doc) => /finance/i.test(doc.url) || /finance/i.test(doc.id));
      const top = financeDocs.ranked[0];
      if (top) {
        used.add(top.doc.id);
        return result({ intent: 'finance', matched: true, score: top.score, bucket: bucket(Math.max(top.score, THRESHOLD)), answer: answerFromDoc(top.doc), related: relatedFor(financeDocs.ranked, used) });
      }
    }

    // 6. Opening hours, phone, email, address, WhatsApp: straight from the business details
    if (PATTERNS.whatsapp.test(normalised)) return factAnswer(index, 'whatsapp');
    if (PATTERNS.hours.test(normalised)) return factAnswer(index, 'hours');
    if (PATTERNS.email.test(normalised)) return factAnswer(index, 'email');
    if (PATTERNS.phone.test(normalised)) return factAnswer(index, 'phone');
    if (PATTERNS.address.test(normalised) && !PATTERNS.coverage.test(normalised)) return factAnswer(index, 'address');

    // 7. Do you cover X?
    const coverage = coverageFor(index, raw);
    if (coverage && coverage.status !== 'unknown') {
      return result({
        intent: 'coverage',
        matched: true,
        score: 1,
        bucket: 'high',
        answer: coverage.answer,
        links: coverage.links || [],
        place: coverage.place,
        coverageStatus: coverage.status,
      });
    }
    if ((coverage && coverage.status === 'unknown') || (PATTERNS.coverage.test(normalised) && /\b(cover|work in|install in|come to|travel to|operate in|serve)\b/.test(normalised) && (!best || best.score < THRESHOLD + 0.15))) {
      return result({ intent: 'coverage', matched: false, handoff: true, score: best ? best.score : 0, bucket: 'none' });
    }

    // 8. Everything else: the best FAQ, page, specification row, statement or guide
    const domain = (token) => Boolean(CONCEPTS[token]) || PRODUCT_TOKENS.has(token);
    const enough = scored.queryTokens.length === 1 ? domain(scored.queryTokens[0]) : best && best.matched >= 2;
    if (best && enough && best.score >= THRESHOLD) {
      used.add(best.doc.id);
      return result({ intent: 'content', matched: true, score: best.score, bucket: bucket(best.score), answer: answerFromDoc(best.doc), related: relatedFor(scored.ranked, used) });
    }

    return result({ intent: 'content', matched: false, handoff: true, score: best ? best.score : 0, bucket: bucket(best ? best.score : 0) });
  }

  /* ---------------- Suggested questions for where the card was opened ---------------- */

  /* After a budget estimate (NEXT-STEPS Step L wording). Each is shown only if it has an answer. */
  const AFTER_ESTIMATE = ['What happens at the survey?', 'How long will installation take?', 'Can I pay monthly?'];

  function suggestions(index, context) {
    const ctx = context || {};
    const out = [];
    /* Two suggestions that ask the same thing in different words: keep the first. */
    const similar = (a, b) => {
      const left = new Set(tokens(a));
      const right = new Set(tokens(b));
      const shared = [...left].filter((token) => right.has(token)).length;
      return shared / Math.max(1, new Set([...left, ...right]).size) >= 0.6;
    };
    const add = (question) => {
      if (question && out.length < 6 && !out.includes(question) && !out.some((existing) => similar(existing, question))) out.push(question);
    };
    const answers = (question) => {
      const reply = answer(index, question, ctx);
      return reply.matched && !reply.handoff;
    };

    if (ctx.estimate) {
      AFTER_ESTIMATE.forEach((question) => {
        if (question === 'Can I pay monthly?' && !ctx.financeOn) return;
        if (answers(question)) add(question);
      });
    }

    const page = index.pages.find((item) => item.handle === ctx.handle);
    if (page && page.faqs && page.faqs.length) {
      page.faqs.forEach((id) => {
        const faq = index.faqs.find((item) => item.id === id);
        if (faq) add(faq.question);
      });
    } else if (ctx.guide) {
      index.faqs.filter((item) => item.page === ctx.guide).forEach((faq) => add(faq.question));
    }

    if (out.length < 4) {
      index.faqs.filter((item) => item.page === 'home').forEach((faq) => add(faq.question));
      ['Is the site survey free?', 'What are your opening hours?', 'Which areas do you cover?'].forEach((question) => {
        if (answers(question)) add(question);
      });
    }
    return out.slice(0, 6);
  }

  const engine = {
    THRESHOLD,
    decodeEntities,
    cleanText,
    normalise,
    tokens,
    analyticsText,
    prepare,
    scoreDocs,
    coverageFor,
    answer,
    suggestions,
    bucket,
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = engine;
    return;
  }

  root.AlaskaColdAsk = engine;
})(typeof window !== 'undefined' ? window : globalThis);
